import { useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import PublicKpiStrip from "@/components/landing/PublicKpiStrip";
import { SITE_URL } from "@/lib/siteConfig";

const TITLE = "Investors | Myelina Health";
const DESC =
  "How Myelina Health thinks about capital, cadence, and the metrics we protect. Written for investors who care about mission-aligned MS software.";

const Investors = () => {
  useEffect(() => {
    document.title = TITLE;
    const setMeta = (name: string, content: string, attr: "name" | "property" = "name") => {
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    setMeta("description", DESC);
    setMeta("og:title", TITLE, "property");
    setMeta("og:description", DESC, "property");
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", `${SITE_URL}/investors`);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main>
        <section className="container mx-auto px-6 sm:px-10 lg:px-16 py-24 max-w-3xl">
          <p className="text-[11px] tracking-[0.28em] uppercase text-muted-foreground mb-4">
            Investors
          </p>
          <h1 className="font-display text-5xl md:text-6xl tracking-tight mb-6">
            Patient-first, then patient-scale.
          </h1>
          <p className="lead-italic text-foreground/80 mb-10 text-lg">
            Myelina Health is building the energy operating system for the ~2.8M people living with MS.
            We're capital-efficient, invite-only during pilot, and every KPI we track ties back to a
            promise we made to a person.
          </p>

          <section className="space-y-10 text-[16px] leading-[1.8] text-foreground/85">
            <div>
              <h2 className="font-display text-2xl mb-3 text-foreground">What we're building</h2>
              <p>
                A daily-use companion that turns 30 seconds of check-in into an honest map of a
                person's energy, the windows worth protecting, the habits that tend to move with a
                harder day, and a shareable summary their care team actually reads. Wellness tool,
                not a medical device. See our{" "}
                <Link to="/model-card" className="underline">model card</Link> and{" "}
                <Link to="/trust" className="underline">trust page</Link>.
              </p>
            </div>

            <div>
              <h2 className="font-display text-2xl mb-3 text-foreground">Why now</h2>
              <ul className="list-disc pl-6 space-y-2">
                <li>MS prevalence is growing globally; fatigue remains the #1 reported symptom and the least well-served in software.</li>
                <li>Wearable coverage in this cohort has crossed the useful-signal threshold.</li>
                <li>Small on-device and edge LLMs finally let us do private, per-user pattern language without shipping data to model providers for training.</li>
              </ul>
            </div>

            <div>
              <h2 className="font-display text-2xl mb-3 text-foreground">How we measure ourselves</h2>
              <p>
                The metrics below are the ones we publish. We update them quarterly. The intent is
                to make it obvious when we drift.
              </p>
            </div>
          </section>
        </section>

        <PublicKpiStrip />

        <section className="container mx-auto px-6 sm:px-10 lg:px-16 py-20 max-w-3xl">
          <div className="space-y-10 text-[16px] leading-[1.8] text-foreground/85">
            <div>
              <h2 className="font-display text-2xl mb-3 text-foreground">Cadence &amp; capital</h2>
              <ul className="list-disc pl-6 space-y-2">
                <li>Public <Link to="/changelog" className="underline">changelog</Link> shipped weekly.</li>
                <li>Invite-only pilot, expanding through our <Link to="/waitlist" className="underline">waitlist</Link> with referral skip-the-line.</li>
                <li>Clinical review via our <Link to="/advisors" className="underline">Clinical Advisory Board</Link>.</li>
                <li>Runway plan biased toward evidence over paid growth until retention KPIs clear internal thresholds.</li>
              </ul>
            </div>

            <div>
              <h2 className="font-display text-2xl mb-3 text-foreground">What we won't do</h2>
              <ul className="list-disc pl-6 space-y-2">
                <li>Sell user data. Ever.</li>
                <li>Ship engagement mechanics that punish the harder days.</li>
                <li>Claim medical outcomes we haven't earned.</li>
                <li>Take capital that requires we break any of the above.</li>
              </ul>
            </div>

            <div className="rounded-2xl border border-primary/20 bg-primary/5 p-8 text-center">
              <h2 className="font-display text-2xl mb-3">Talk to us</h2>
              <p className="text-foreground/75 mb-6 max-w-xl mx-auto">
                We share a mission-aligned investor memo on request. Please email a short note about
                your fund's fit with our patient-first stance.
              </p>
              <a
                href="mailto:investors@myelinahealth.com?subject=Investor%20memo%20request"
                className="inline-flex items-center justify-center min-h-[48px] px-6 rounded-xl bg-primary text-primary-foreground font-medium text-[14px] hover:opacity-90 transition-opacity"
              >
                Email investors@myelinahealth.com
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Investors;
